/* eslint-env browser */
// Ethiopic-safe exports for the portal (M15).
//
// - exportCsv({ columns, rows, fileName }): UTF-8 CSV with a BOM (Excel shows
//   Amharic correctly), Addis Ababa 12-hour dates, formula-injection guard.
// - printTable({ title, subtitle, columns, rows, fileName, footnote, landscape }):
//   an HTML table printed through a hidden in-page iframe with Noto Sans
//   Ethiopic loaded, so "Save as PDF" in the print dialog keeps Amharic names.
//   Use it instead of material-table's jsPDF export (Helvetica only, Amharic
//   comes out blank): options.exportPdf = (cols, data) => printTable({...}).
// - printDocument({ html, title }): print a full HTML document (a receipt
//   template) the same way, with the Ethiopic fonts injected.
// - formatAddisDate / formatAddisTime / formatAddisDateTime / addisDateStamp /
//   datedFileName: dates in Addis Ababa time (UTC+3, no daylight saving) on a
//   12-hour clock: "25 Oct 2026", "2:31 pm", "25 Oct 2026, 2:31 pm",
//   "2026-10-25", "bookings_2026-10-25.csv".
// No popups (window.open is blocked on shared PCs) and no reads or writes.

import { ethiopicFontFaceCss, ensureEthiopicFonts, FONT_STACK } from '../theme/fonts';

export const ADDIS_OFFSET_MS = 3 * 60 * 60 * 1000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const toDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  let d;
  if (value instanceof Date) d = value;
  else if (typeof value === 'number') d = new Date(value);
  else if (typeof value === 'string' && /^\d{9,}$/.test(value.trim())) d = new Date(Number(value.trim()));
  else d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const addisParts = (value) => {
  const d = toDate(value);
  if (!d) return null;
  const a = new Date(d.getTime() + ADDIS_OFFSET_MS);
  return {
    year: a.getUTCFullYear(),
    month: a.getUTCMonth(),
    day: a.getUTCDate(),
    weekday: a.getUTCDay(),
    hour: a.getUTCHours(),
    minute: a.getUTCMinutes(),
  };
};

const pad2 = (n) => (n < 10 ? `0${n}` : String(n));

/** "25 Oct 2026" (weekday: true -> "Sun 25 Oct 2026"); '' for an empty or bad value. */
export const formatAddisDate = (value, { weekday = false } = {}) => {
  const p = addisParts(value);
  if (!p) return '';
  return `${weekday ? `${WEEKDAYS[p.weekday]} ` : ''}${p.day} ${MONTHS[p.month]} ${p.year}`;
};

/** "2:31 pm" in Addis Ababa time. */
export const formatAddisTime = (value) => {
  const p = addisParts(value);
  if (!p) return '';
  const h12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
  return `${h12}:${pad2(p.minute)} ${p.hour < 12 ? 'am' : 'pm'}`;
};

/** "25 Oct 2026, 2:31 pm" in Addis Ababa time. */
export const formatAddisDateTime = (value, opts) => {
  const date = formatAddisDate(value, opts);
  return date ? `${date}, ${formatAddisTime(value)}` : '';
};

/** "2026-10-25": the Addis Ababa calendar date, for file names. */
export const addisDateStamp = (value = Date.now()) => {
  const p = addisParts(value);
  if (!p) return '';
  return `${p.year}-${pad2(p.month + 1)}-${pad2(p.day)}`;
};

/** datedFileName('bookings', 'csv') -> "bookings_2026-10-25.csv" (Addis date of `when`, default now). */
export const datedFileName = (base, ext, when) => {
  const safe = String(base || 'export').trim().replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_');
  const stamp = addisDateStamp(when === undefined ? Date.now() : when);
  return `${safe}${stamp ? `_${stamp}` : ''}${ext ? `.${ext}` : ''}`;
};

export const escapeHtml = (value) =>
  String(value === null || value === undefined ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const getByPath = (row, path) => {
  if (!row || !path) return undefined;
  if (Object.prototype.hasOwnProperty.call(row, path)) return row[path];
  return String(path)
    .split('.')
    .reduce((obj, key) => (obj && obj[key] !== undefined ? obj[key] : undefined), row);
};

/**
 * Columns: [{ title, field, value?: (row) => any, type?: 'date' | 'time' | 'datetime' | 'number', align?: 'right' }]
 * `value` wins over `field`; date types are formatted in Addis Ababa time.
 */
export const cellText = (column, row) => {
  const raw = typeof column.value === 'function' ? column.value(row) : getByPath(row, column.field);
  if (raw === null || raw === undefined) return '';
  if (column.type === 'datetime') return formatAddisDateTime(raw);
  if (column.type === 'date') return formatAddisDate(raw);
  if (column.type === 'time') return formatAddisTime(raw);
  if (typeof raw === 'object') {
    try {
      return JSON.stringify(raw);
    } catch (e) {
      return '';
    }
  }
  return String(raw);
};

// A cell that starts like a formula (=, @, or +/- not followed by a plain
// number) gets a leading apostrophe so spreadsheet apps do not run it.
// Phone numbers (+251 91 ...) and negative amounts stay as they are.
const needsFormulaGuard = (s) => /^[=@\t\r]/.test(s) || (/^[+-]/.test(s) && !/^[+-][\d\s().,]*$/.test(s));

const csvCell = (value) => {
  let s = value === null || value === undefined ? '' : String(value);
  if (needsFormulaGuard(s)) s = `'${s}`;
  if (/[",\r\n]/.test(s) || /^\s|\s$/.test(s)) s = `"${s.replace(/"/g, '""')}"`;
  return s;
};

/** CSV text (no BOM) for columns and rows; see exportCsv. */
export const toCsv = ({ columns, rows }) => {
  const cols = Array.isArray(columns) ? columns : [];
  const lines = [cols.map((col) => csvCell(col.title)).join(',')];
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    lines.push(cols.map((col) => csvCell(cellText(col, row))).join(','));
  });
  return lines.join('\r\n');
};

const triggerDownload = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 1000);
};

/**
 * Download rows as a CSV that Excel opens with Amharic intact.
 *   exportCsv({ columns, rows, fileName: datedFileName('bookings', 'csv') })
 */
export const exportCsv = ({ columns, rows, fileName }) => {
  const name = fileName || datedFileName('export', 'csv');
  const blob = new Blob(['\uFEFF', toCsv({ columns, rows })], { type: 'text/csv;charset=utf-8' });
  triggerDownload(blob, /\.csv$/i.test(name) ? name : `${name}.csv`);
};

// A material-table `lookup` column exports the words the table shows
// (lookup[value]), not the stored key; a value with no text entry stays as is.
const lookupValue = (col) => (row) => {
  const raw = getByPath(row, col.field);
  if (raw === null || raw === undefined || !Object.prototype.hasOwnProperty.call(col.lookup, raw)) return raw;
  const shown = col.lookup[raw];
  return typeof shown === 'string' || typeof shown === 'number' ? shown : raw;
};

/**
 * material-table columns -> exportUtils columns: drops hidden columns and
 * those with export: false; a column whose title is not text needs
 * exportTitle (or falls back to its field name). exportValue(row) wins;
 * otherwise a lookup column exports its looked-up words.
 */
export const columnsFromMaterialTable = (columns) =>
  (Array.isArray(columns) ? columns : [])
    .filter((col) => col && !col.hidden && col.export !== false && (col.field || typeof col.exportValue === 'function'))
    .map((col) => ({
      title: typeof col.exportTitle === 'string' ? col.exportTitle : typeof col.title === 'string' ? col.title : String(col.field || ''),
      field: col.field,
      value:
        typeof col.exportValue === 'function'
          ? col.exportValue
          : col.lookup && typeof col.lookup === 'object' && col.field
            ? lookupValue(col)
            : undefined,
      type: col.exportType,
      align: col.type === 'numeric' || col.type === 'currency' ? 'right' : col.align,
    }));

const PRINT_CSS = (landscape) => `
@page { size: A4 ${landscape ? 'landscape' : 'portrait'}; margin: 14mm 12mm; }
html { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
body { font-family: ${FONT_STACK}; color: #1F2937; font-size: 10.5pt; line-height: 1.45; margin: 0; }
h1 { font-size: 16pt; line-height: 1.35; margin: 0 0 2pt; }
.sub { color: #4B5563; font-size: 10pt; margin: 0 0 10pt; }
table { width: 100%; border-collapse: collapse; }
th, td { text-align: left; padding: 5pt 6pt; border-bottom: 0.75pt solid #D6D9DE; vertical-align: top; overflow-wrap: anywhere; }
th { font-size: 9pt; color: #4B5563; font-weight: 700; }
thead { display: table-header-group; }
tr { break-inside: avoid; page-break-inside: avoid; }
td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
tbody tr:nth-child(even) td { background: #F5F6F8; }
.foot { margin-top: 10pt; color: #5F6672; font-size: 9pt; }
`;

const ROBOTO_CSS_URL = 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap';

const fontHead = () =>
  `<link rel="stylesheet" href="${ROBOTO_CSS_URL}"><style>${ethiopicFontFaceCss({ absoluteUrls: true })}</style>`;

// Print a full HTML document through a hidden iframe on this page.
const printThroughIframe = (fullHtml) =>
  new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('tabindex', '-1');
    iframe.setAttribute('title', 'print');
    Object.assign(iframe.style, {
      position: 'fixed',
      right: '0',
      bottom: '0',
      width: '0',
      height: '0',
      border: '0',
      opacity: '0',
      pointerEvents: 'none',
    });
    document.body.appendChild(iframe);
    let finished = false;
    const cleanup = () => {
      if (finished) return;
      finished = true;
      setTimeout(() => {
        try {
          iframe.remove();
        } catch (e) {
          // already gone
        }
        resolve();
      }, 500);
    };
    const doc = iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document);
    if (!doc) {
      cleanup();
      return;
    }
    doc.open();
    doc.write(fullHtml);
    doc.close();
    const win = iframe.contentWindow;
    const go = async () => {
      await ensureEthiopicFonts([400, 700], 4000, doc);
      try {
        if (doc.fonts && doc.fonts.ready) {
          await Promise.race([doc.fonts.ready, new Promise((r) => setTimeout(r, 3000))]);
        }
      } catch (e) {
        // print with whatever has loaded
      }
      try {
        win.addEventListener('afterprint', cleanup);
        win.focus();
        win.print();
      } catch (e) {
        cleanup();
        return;
      }
      // Browsers whose print() returns at once still get a minute.
      setTimeout(cleanup, 60000);
    };
    // Start once: on load, or after 6 s if a stylesheet never answers
    // (Google Fonts blocked or very slow), so the print is never held back.
    let started = false;
    const start = () => {
      if (started || finished) return;
      started = true;
      go();
    };
    if (doc.readyState === 'complete') start();
    else {
      iframe.addEventListener('load', start, { once: true });
      setTimeout(start, 6000);
    }
  });

/**
 * Print rows as a table (and let the person "Save as PDF") with Amharic
 * intact. The document title becomes fileName, which browsers propose as
 * the PDF's name.
 *   printTable({ title: t('booking_history'), subtitle: '412 bookings, last 7 days',
 *                columns, rows, fileName: datedFileName('bookings', ''), landscape: true })
 * Resolves when the print dialog closes.
 */
export const printTable = ({ title = '', subtitle = '', columns, rows, fileName, footnote = '', landscape = false, lang, css = '' }) => {
  const cols = Array.isArray(columns) ? columns : [];
  const head = cols
    .map((col) => `<th${col.align === 'right' || col.type === 'number' ? ' class="num"' : ''}>${escapeHtml(col.title)}</th>`)
    .join('');
  const body = (Array.isArray(rows) ? rows : [])
    .map(
      (row) =>
        `<tr>${cols
          .map((col) => `<td${col.align === 'right' || col.type === 'number' ? ' class="num"' : ''}>${escapeHtml(cellText(col, row))}</td>`)
          .join('')}</tr>`
    )
    .join('');
  const docTitle = fileName ? String(fileName).replace(/\.[a-z0-9]+$/i, '') : title;
  const pageLang = lang || document.documentElement.getAttribute('lang') || 'en';
  const html =
    `<!DOCTYPE html><html lang="${escapeHtml(pageLang)}"><head><meta charset="utf-8"><title>${escapeHtml(docTitle)}</title>` +
    `${fontHead()}<style>${PRINT_CSS(landscape)}${css}</style></head><body>` +
    `${title ? `<h1>${escapeHtml(title)}</h1>` : ''}${subtitle ? `<p class="sub">${escapeHtml(subtitle)}</p>` : ''}` +
    `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>` +
    `${footnote ? `<p class="foot">${escapeHtml(footnote)}</p>` : ''}</body></html>`;
  return printThroughIframe(html);
};

/**
 * Print an existing HTML document (for example a receipt template) with the
 * Ethiopic fonts injected. Templates should name the stack in their CSS:
 * font-family: "Roboto", "Noto Sans Ethiopic", Arial, sans-serif.
 */
export const printDocument = ({ html, title }) => {
  let doc = String(html || '');
  const inject = `${fontHead()}${title ? `<title>${escapeHtml(title)}</title>` : ''}`;
  if (/<head[^>]*>/i.test(doc)) doc = doc.replace(/<head([^>]*)>/i, `<head$1><meta charset="utf-8">${inject}`);
  else doc = `<!DOCTYPE html><html><head><meta charset="utf-8">${inject}</head><body>${doc}</body></html>`;
  return printThroughIframe(doc);
};

export { ensureEthiopicFonts, FONT_STACK };
